/**
 * Barra de acciones de servidor.
 *
 * Las acciones son los verbos del ERP: liquidar un período, emitir expensas,
 * confirmar un pedido, validar un asiento. La lógica vive ENTERA en el backend;
 * acá solo se dibuja lo que la metadata declara y se dispara
 * `POST /api/v1/model/<modelo>/<id>/action/<nombre>/`.
 *
 * Cinco reglas que hacen que esto no se convierta en una segunda máquina de
 * estados que se desincroniza con la de verdad:
 *
 * 1. **`enabled` llega evaluado.** Si el backend dice que no se puede, el botón
 *    se ve deshabilitado con el motivo del servidor. El frontend no reimplementa
 *    "un período cerrado no se puede liquidar": no lo sabe y no tiene por qué.
 *    Para que ese `enabled` sea el del REGISTRO y no el del modelo, la metadata
 *    del formulario se pide con `?id=` (ver `useMetaQuery`).
 * 2. **Los nombres son los del backend.** `confirm` es un booleano y el texto va
 *    en `confirm_message`; lo irreversible es `destructive`; el alcance es
 *    `target`. Renombrarlos de este lado fue el bug que abría un diálogo de
 *    confirmación vacío en las 104 acciones del catálogo.
 * 3. **Los parámetros se piden antes de disparar**, no después del 400: el
 *    backend valida `ActionParam` con `coerce()` y una acción con parámetros
 *    obligatorios (`motivo`, `porcentaje`, `desde`/`hasta`) no puede ejecutarse
 *    a ciegas. Ver `ActionDialog`.
 * 4. **Al volver se refresca el modelo entero**, no solo el registro tocado: una
 *    acción de servidor puede crear cargos, mover totales y cambiar de columna en
 *    el tablero. Refrescar de menos deja pantallas que se contradicen.
 * 5. **Degrada a nada.** Si `spec.actions` es `undefined` la barra no dibuja
 *    nada. Ninguna pantalla depende de que exista.
 * 6. **El que cuenta qué pasó es el servidor.** La respuesta trae `message` con
 *    lo que la acción hizo de verdad y es eso lo que se muestra. En las
 *    acciones de reporte —sumas y saldos, estado de resultados, libro de IVA,
 *    antigüedad de saldos, comparar proveedores— ese mensaje **es** el
 *    resultado: no cambian ningún registro, así que un «listo» genérico deja al
 *    botón sin efecto visible. Con varios registros seleccionados se muestra el
 *    recuento, porque son N mensajes y no uno.
 *
 * Sobre una selección, una acción `target="record"` se ejecuta de a un registro
 * por vez —igual que archivar y eliminar en la lista— y una `target="selection"`
 * se manda de una sola vez al endpoint de colección, que es lo que su servicio
 * espera recibir.
 */

import { useMutation } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { recordPath } from '@/components/shell/menu-utils';
import { notify } from '@/components/shell/toast';
import { ApiError, api } from '@/lib/api';
import type { ActionResult, ActionScope, JsonValue, ModelAction, ModelSpec } from '@/lib/types';
import { ActionDialog, cleanParamValues, initialParamValues, needsDialog, type ActionParamValues } from '@/views/ActionDialog';
import {
  DETALLE_SUCIO,
  motivoDelServidor,
  salidaDeGuardado,
  type AccionNoDisponible,
  type EstadoDeGuardado,
} from '@/views/MotivoBloqueo';
import { AgingResultDialog, leerAntiguedad, type AntiguedadJson } from '@/views/AgingResultDialog';
import { motivoDe } from '@/views/errores';
import { LinkResultDialog, leerEnlace, type EnlaceJson } from '@/views/LinkResultDialog';
import { useInvalidateData } from '@/views/queries';
import { ReportExcelButton, ReportPdfButton } from '@/views/ReportScreen';
import { leerReporteTabular, TabularReportDialog } from '@/views/TabularReport';
import { VentaRapidaDialog } from '@/views/VentaRapidaDialog';
import { Toolbar, type ToolbarItem } from '@/views/Toolbar';
import type { TabularReportData } from '@/lib/types';

export interface ActionBarProps {
  spec: ModelSpec;
  /** Registros sobre los que se ejecuta. Vacío no dibuja nada. */
  ids: number[];
  /** Dónde se está dibujando: decide qué acciones aplican. */
  scope: ActionScope;
  size?: 'sm' | 'md';
  /**
   * Motivo por el que TODAS las acciones están bloqueadas ahora mismo
   * (típicamente: hay cambios sin guardar). Se muestra en el globo del botón, no
   * en el `title`: un `<button disabled>` no recibe foco ni emite eventos, así
   * que el tooltip nativo no existía para quien navega con teclado ni en táctil.
   * Ver `ControlBloqueado`.
   */
  blockedReason?: string | null;
  /**
   * El guardado de la ficha, para poder ofrecer la salida en el mismo clic. Se
   * pasa explícito —y no por contexto— porque el formulario llama a este hook en
   * su propio render, antes de montar el proveedor.
   */
  guardado?: EstadoDeGuardado | null;
  /** Se llama después de una ejecución exitosa. */
  onDone?: () => void;
}

/**
 * Una acción `selection` recibe la lista entera de una vez, así que en el
 * formulario —donde hay un registro y nada que agrupar— no tiene sentido y no
 * se ofrece. Una `record` sirve en los dos lados.
 */
function aplicaEn(accion: ModelAction, scope: ActionScope): boolean {
  const target = accion.target ?? 'record';
  return scope === 'selection' || target === 'record';
}

/**
 * ¿Esta acción no se puede hacer ahora y por lo tanto no se dibuja?
 *
 * **Un solo criterio: lo que el backend deshabilitó, no se muestra.** El manual
 * (01 §1.4) promete «sólo aparecen los que se pueden hacer ahora», y hasta acá
 * eso valía a medias: se ocultaba lo que no aplicaba al estado (la acción
 * declara `available_domain` y el registro no cae dentro) y se dibujaba en gris
 * lo que estaba bloqueado por permiso. Resultado: la misma pantalla se
 * comportaba de dos maneras según de dónde viniera el `enabled=false`, y quien
 * no tenía permiso veía una botonera de botones muertos que no iba a poder
 * apretar nunca —ni ese día ni el mes que viene—.
 *
 * Ahora es uno solo, y en las dos direcciones:
 *
 * - **no aplica al estado** (una factura confirmada no se vuelve a confirmar):
 *   se oculta, porque volverá a aparecer sola cuando el estado cambie;
 * - **no tenés el permiso** (el modelo no es escribible, o una regla de registro
 *   niega la escritura): tampoco aparece. Un botón que no vas a poder apretar es
 *   ruido, y además cuenta cómo está organizada la empresa. Es exactamente el
 *   criterio que la metadata ya aplica con los `groups` de una acción, que
 *   directamente no llegan al cliente.
 *
 * Lo que **sí** sigue visible y deshabilitado es `blockedReason` —hay cambios
 * sin guardar—: eso no es un estado del registro ni un permiso, es una condición
 * de la pantalla que la persona puede levantar en un clic, y esconder el botón
 * ahí la dejaría buscando adónde se fue «Confirmar».
 *
 * Lo que cambió: **ocultarlo ya no es hacerlo desaparecer sin rastro.** El
 * backend viene mandando, para cada una de estas, un `disabled_reason` redactado
 * término por término (««Registrar cobro» no está disponible: «Estado» tiene que
 * ser «Confirmada» y es «Pagada»…») que el frontend descartaba entero. Ahora esa
 * lista sale por `ocultas` y la cabecera la muestra en una línea plegable. El
 * botón muerto sigue sin dibujarse; el motivo, sí.
 */
function noSeDibuja(accion: ModelAction): boolean {
  return accion.enabled === false;
}

/**
 * ¿Se puede encadenar un guardado con esta acción sin riesgo?
 *
 * **Sólo si la acción no escribe nada.** `readonly` en la metadata quiere decir
 * exactamente eso: calcula y devuelve, la API le pide permiso de lectura y no de
 * escritura (ver `ServerAction.readonly` en el backend). Un cálculo, una
 * consulta, un cuadro.
 *
 * Para todo lo demás —confirmar, emitir, facturar, asentar, anular— se guarda y
 * se suelta: el botón queda habilitado y con el foco, y lo aprieta la persona.
 * La regla del proyecto no admite matices acá: un guardado que encadena una
 * emisión es exactamente lo que este trabajo no puede introducir, y «esta acción
 * en particular parece inofensiva» es el razonamiento con el que se introduce.
 */
function sePuedeEncadenar(accion: ModelAction): boolean {
  return accion.readonly === true && accion.destructive !== true && accion.confirm !== true;
}

/**
 * ¿Esta accion se completa con el SELECTOR DE PRODUCTOS en vez del dialogo generico?
 *
 * Se decide por la FORMA de la accion y no por su nombre, con el mismo criterio
 * con el que `leerAntiguedad` reconoce un informe de cuenta corriente: una accion
 * sobre un contacto cuyo unico dato obligatorio es un `text` llamado `renglones`
 * es, por definicion, una que espera una lista de productos marcados. Pedirla en
 * un `<textarea>` —que es lo que hace el dialogo generico con un `text`— seria
 * pedirle al vendedor que tipee JSON a mano.
 *
 * El selector no reimplementa nada del disparo: arma el JSON y llama a la misma
 * accion por la misma API. Ver `VentaRapidaDialog`.
 */
function usaSelectorDeProductos(accion: ModelAction, model: string): boolean {
  if (model !== 'base.partner') return false;
  const params = accion.params ?? [];
  return params.some((p) => p.name === 'renglones' && p.type === 'text' && p.required);
}

/** A donde llevar al usuario despues de la accion, si el servidor lo pidio. */
interface Destino {
  model: string;
  id: number;
}

/**
 * Lee la clave `open` que puede devolver una accion: `{model, id}`.
 *
 * Es GENERICA — cualquier accion de cualquier modulo puede devolverla y no hay
 * una linea de frontend por caso. Existe porque hay operaciones que **crean el
 * registro que uno queria y despues hay que ir a buscarlo a mano**: «Registrar
 * cobro» sobre una factura arma el cobro con el cliente, el saldo pendiente y la
 * imputacion ya cargados, y dejarlo escondido en una lista sería devolverle al
 * usuario los tres clicks que la accion vino a ahorrarle.
 *
 * Viaja en `results[0].data.open`, que es donde la API pone lo que devolvio el
 * servicio. Se lee con desconfianza: cualquier forma inesperada se ignora y la
 * accion se comporta como cualquier otra.
 */
function destinoDeApertura(resultado: ActionResult | undefined): Destino | null {
  const resultados = resultado?.results;
  if (!Array.isArray(resultados) || resultados.length !== 1) return null;
  const datos = (resultados[0] as { data?: unknown } | undefined)?.data;
  if (datos === null || typeof datos !== 'object') return null;
  const abrir = (datos as { open?: unknown }).open;
  if (abrir === null || typeof abrir !== 'object') return null;
  const { model, id } = abrir as { model?: unknown; id?: unknown };
  if (typeof model !== 'string' || model === '' || typeof id !== 'number') return null;
  return { model, id };
}

/**
 * Lo que devuelve `useActionItems`: los botones DESCRITOS y los diálogos que
 * hay que dejar montados.
 *
 * Existe para que la cabecera del formulario pueda mezclar estos botones con
 * los de reportes, los de correo y los suyos propios en UNA sola botonera con
 * un solo menú «Más» (ver `Toolbar`), en vez de tres barras que se pelean por
 * el ancho y le comen el título al registro. `ActionBar` sigue existiendo y
 * dibujando lo mismo de siempre para la lista, que no tiene ese problema.
 */
export interface BarraDeAcciones {
  items: ToolbarItem[];
  /** Diálogos y paneles de resultado. Se renderizan una sola vez, donde sea. */
  dialogs: ReactNode;
  /**
   * Las que el servidor deshabilitó, con el motivo que mandó. No se dibujan como
   * botones —serían botones muertos— pero tienen que poder leerse en algún lado:
   * ver `AccionesNoDisponibles`.
   */
  ocultas: AccionNoDisponible[];
}

export function useActionItems({
  spec,
  ids,
  scope,
  blockedReason = null,
  guardado = null,
  onDone,
}: Omit<ActionBarProps, 'size'>): BarraDeAcciones {
  // Una accion de servidor escribe donde quiere —las lineas del extracto, los
  // apuntes del asiento, los cargos por unidad—, no solo en el modelo del boton.
  // Ver `useInvalidateData`.
  const invalidar = useInvalidateData();
  const navegar = useNavigate();
  const [enDialogo, setEnDialogo] = useState<ModelAction | null>(null);
  /**
   * Por qué el servidor rechazó la acción que se disparó DESDE el diálogo.
   *
   * Hasta acá el `onError` hacía `setEnDialogo(null)`: el diálogo se cerraba de
   * golpe y el motivo iba a un aviso flotante en la esquina. Para la persona era
   * exactamente «apreté y no pasó nada» —el diálogo desaparece, la pantalla
   * queda igual— y encima perdía los parámetros ya tipeados (el motivo de una
   * baja, la fecha de las facturas) y tenía que reabrir y reescribir todo.
   *
   * Ahora el diálogo queda abierto con lo tipeado adentro y se hace cargo del
   * motivo. `campos` son los errores que el 400 puso sobre un parámetro concreto.
   */
  const [errorDeAccion, setErrorDeAccion] = useState<{
    motivo: string;
    campos: Record<string, string>;
  } | null>(null);
  // Contacto sobre el que se abrio el selector de productos de la venta rapida.
  // Es `null` mientras esta cerrado; ver `usaSelectorDeProductos`.
  const [vendiendoA, setVendiendoA] = useState<number | null>(null);
  // Resultado de una acción de antigüedad de saldos (cuenta corriente): se
  // muestra como panel formateado, no como toast con el número crudo.
  const [panelAntiguedad, setPanelAntiguedad] = useState<{ titulo: string; datos: AntiguedadJson } | null>(null);
  // Idem con las acciones cuyo resultado ES un enlace para pasarle a alguien:
  // un toast no se puede copiar, y sin SMTP el enlace es lo unico que hay. Se
  // detecta por la forma del dato, asi que no hace falta ninguna accion
  // concreta para que ande. Ver `LinkResultDialog`.
  const [panelEnlace, setPanelEnlace] = useState<{ titulo: string; datos: EnlaceJson } | null>(null);
  // Resultado de una accion que devolvio un CUADRO (`data.table`): los cinco
  // estados contables y cualquier reporte tabular que llegue despues. Se dibuja
  // con el renderer generico; el frontend no sabe de que reporte se trata. Ver
  // `TabularReport`.
  // Viaja tambien el registro y los parametros con los que se calculo, que es lo
  // que necesita el boton «Exportar a Excel» para volver a pedir EL MISMO cuadro
  // como planilla. Ver `ReportExcelButton`.
  const [panelReporte, setPanelReporte] = useState<{
    titulo: string;
    datos: TabularReportData;
    id: number;
    accion: string;
    params: Record<string, JsonValue>;
  } | null>(null);

  const ejecutar = useMutation({
    mutationFn: async (entrada: { accion: ModelAction; params: ActionParamValues }) => {
      const { accion, params } = entrada;
      if ((accion.target ?? 'record') === 'selection') {
        const resultado = await api.callCollectionAction(spec.model, accion.name, ids, params);
        return { accion, params, resultado };
      }
      // Sobre varios registros son N llamadas; se guarda la ultima porque es la
      // unica que puede mostrarse sin inventar un resumen que el servidor no dio.
      let resultado: ActionResult | undefined;
      for (const id of ids) {
        resultado = await api.callAction(spec.model, id, accion.name, params);
      }
      return { accion, params, resultado };
    },
    onSuccess: ({ accion, params, resultado }) => {
      setEnDialogo(null);
      setErrorDeAccion(null);
      void invalidar(spec.model);
      // Las acciones de antigüedad de saldos (cuenta corriente) devuelven el
      // saldo repartido por tramos: se muestra como panel legible en vez de un
      // toast con el número crudo. Solo tiene sentido sobre un contacto.
      // Primero el cuadro: es la forma GENERICA y la que cubre a todos los
      // reportes nuevos. La antiguedad de saldos sigue con su panel propio
      // porque su dibujo —una banda por tramo— no es una tabla de columnas.
      const cuadro = ids.length === 1 ? leerReporteTabular(resultado) : null;
      if (cuadro !== null && ids[0] !== undefined) {
        setPanelReporte({
          titulo: accion.label,
          datos: cuadro,
          id: ids[0],
          accion: accion.name,
          params: params as Record<string, JsonValue>,
        });
        onDone?.();
        return;
      }
      const antiguedad = ids.length === 1 ? leerAntiguedad(resultado) : null;
      if (antiguedad !== null) {
        setPanelAntiguedad({ titulo: accion.label, datos: antiguedad });
        onDone?.();
        return;
      }
      // Idem para las que devuelven un enlace entregable: el mensaje del
      // servidor va igual al toast, porque cuenta que la cuenta se creo, y el
      // panel se ocupa de que el enlace se pueda copiar.
      const enlace = ids.length === 1 ? leerEnlace(resultado) : null;
      if (enlace !== null) {
        setPanelEnlace({ titulo: accion.label, datos: enlace });
        if (typeof resultado?.message === 'string' && resultado.message.trim() !== '') {
          notify.success(accion.label, resultado.message.trim());
        }
        onDone?.();
        return;
      }
      // Con varios registros la ultima respuesta habla de UNO solo: mostrarla
      // como si resumiera la tanda seria mentir. Ahi va el recuento y nada mas.
      const delServidor =
        ids.length === 1 && typeof resultado?.message === 'string' && resultado.message.trim() !== ''
          ? resultado.message.trim()
          : null;
      if (delServidor !== null) {
        notify.success(accion.label, delServidor);
      } else {
        notify.success(
          ids.length === 1
            ? `${accion.label}: listo`
            : `${accion.label}: ${ids.length} ${spec.label_plural.toLowerCase()}`,
        );
      }
      onDone?.();
      // Si la accion creo un registro y pidio abrirlo, se navega DESPUES de
      // avisar: el toast sobrevive al cambio de pantalla y explica que se acaba
      // de ver. Ver `destinoDeApertura`.
      const destino = destinoDeApertura(resultado);
      if (destino !== null) navegar(recordPath(destino.model, destino.id));
    },
    onError: (error) => {
      // Con varios registros puede haber quedado a medias: se refresca para que
      // la pantalla muestre el estado real y no el que esperábamos.
      void invalidar(spec.model);

      if (enDialogo !== null) {
        // El diálogo QUEDA ABIERTO, con los parámetros tipeados adentro, y
        // muestra el motivo. Cerrarlo era tirar el trabajo de la persona junto
        // con la explicación de por qué no salió.
        const campos: Record<string, string> = {};
        if (error instanceof ApiError) {
          const nombres = new Set((enDialogo.params ?? []).map((param) => param.name));
          for (const [campo, mensajes] of Object.entries(error.fields)) {
            if (nombres.has(campo)) campos[campo] = mensajes.join(' ');
          }
        }
        setErrorDeAccion({ motivo: motivoDe(error, 'La acción no se pudo ejecutar.'), campos });
        return;
      }

      notify.error('No se pudo ejecutar la acción', motivoDe(error));
    },
  });

  // El orden lo decide el módulo con `sequence`; a igual secuencia, el alfabético
  // deja la botonera estable entre recargas.
  const delAlcance = (spec.actions ?? [])
    .filter((accion) => aplicaEn(accion, scope))
    .slice()
    .sort((a, b) => (a.sequence ?? 10) - (b.sequence ?? 10) || a.label.localeCompare(b.label, 'es'));

  // Un header con sólo lo que se puede hacer ahora, no una fila de botones
  // grises. Ver `noSeDibuja`.
  const acciones = delAlcance.filter((accion) => !noSeDibuja(accion));

  // …y las otras, con el motivo del servidor, para que la cabecera las pueda
  // contar y explicar. Sobre una selección no se listan: sin `?id=` el backend
  // no evalúa el estado (no puede: la selección puede tener registros en
  // estados distintos), así que ahí `enabled=false` sólo puede venir de un
  // permiso y eso no es algo que la persona pueda destrabar.
  const ocultas: AccionNoDisponible[] =
    ids.length === 0 || scope === 'selection'
      ? []
      : delAlcance.filter(noSeDibuja).map((accion) => ({
          clave: `accion-${accion.name}`,
          etiqueta: accion.label,
          motivo: motivoDelServidor(accion.disabled_reason, accion.label),
        }));

  const enCurso = ejecutar.isPending ? ejecutar.variables.accion : undefined;

  const lanzar = (accion: ModelAction): void => {
    // El selector de productos gana sobre el dialogo generico: es el mismo
    // disparo, pero con casillas, precio resuelto y total en vivo en lugar de un
    // `<textarea>` esperando JSON. Solo tiene sentido sobre UN contacto.
    if (usaSelectorDeProductos(accion, spec.model) && ids.length === 1 && ids[0] !== undefined) {
      setVendiendoA(ids[0]);
      return;
    }
    if (needsDialog(accion)) {
      setErrorDeAccion(null);
      setEnDialogo(accion);
      return;
    }
    ejecutar.mutate({ accion, params: cleanParamValues(accion, initialParamValues(accion)) });
  };

  // Sin acciones o sin registros no hay botones, pero los diálogos se siguen
  // devolviendo: son componentes que se dibujan a sí mismos como nada cuando no
  // tienen nada abierto, y desmontarlos a mitad de una ejecución perdería el
  // panel de resultado. Los hooks, además, ya corrieron todos: cortar acá con un
  // `return` temprano sería el bug clásico de orden de hooks.
  const items: ToolbarItem[] =
    ids.length === 0
      ? []
      : acciones.map((accion) => {
          // Todo lo que llega hasta acá está habilitado: lo deshabilitado ya se
          // filtró arriba. Lo único que puede frenarlo ahora es la pantalla
          // (`blockedReason`: hay cambios sin guardar), y eso sí se dice —en el
          // globo, no en el `title`—.
          const ayuda = accion.help !== undefined && accion.help !== '' ? accion.help : null;
          return {
            key: `accion-${accion.name}`,
            label: accion.label,
            variant: accion.destructive === true ? ('danger' as const) : ('secondary' as const),
            disabled: blockedReason !== null,
            loading: enCurso?.name === accion.name,
            title: ayuda,
            reason: blockedReason === null ? null : `${blockedReason} ${DETALLE_SUCIO}`,
            salida:
              blockedReason === null
                ? null
                : salidaDeGuardado(guardado, {
                    continuar: () => {
                      lanzar(accion);
                    },
                    encadena: sePuedeEncadenar(accion),
                    clave: `accion-${accion.name}`,
                  }),
            onClick: () => {
              lanzar(accion);
            },
          };
        });

  const dialogs = (
    <>
      <ActionDialog
        action={enDialogo}
        count={ids.length}
        loading={ejecutar.isPending}
        error={errorDeAccion?.motivo ?? null}
        fieldErrors={errorDeAccion?.campos ?? {}}
        onConfirm={(params) => {
          if (enDialogo !== null) {
            // El motivo del intento anterior no sobrevive al siguiente: dejarlo
            // haría leer como actual algo que quizás ya se corrigió.
            setErrorDeAccion(null);
            ejecutar.mutate({ accion: enDialogo, params });
          }
        }}
        onCancel={() => {
          setEnDialogo(null);
          setErrorDeAccion(null);
        }}
      />

      <AgingResultDialog
        titulo={panelAntiguedad?.titulo ?? ''}
        datos={panelAntiguedad?.datos ?? null}
        {...(panelAntiguedad?.datos.contacto === undefined ? {} : { contactoNombre: panelAntiguedad.datos.contacto })}
        onClose={() => {
          setPanelAntiguedad(null);
        }}
      />

      <VentaRapidaDialog
        partnerId={vendiendoA}
        onClose={() => {
          setVendiendoA(null);
        }}
        onDone={() => {
          // La venta escribio filas de `sales.subscription`; el contacto que se
          // esta mirando muestra su grilla y sus totales.
          void invalidar('sales.subscription');
          void invalidar(spec.model);
          onDone?.();
        }}
      />

      <LinkResultDialog
        titulo={panelEnlace?.titulo ?? ''}
        datos={panelEnlace?.datos ?? null}
        onClose={() => {
          setPanelEnlace(null);
        }}
      />

      <TabularReportDialog
        titulo={panelReporte?.titulo ?? ''}
        datos={panelReporte?.datos ?? null}
        acciones={
          panelReporte === null ? undefined : (
            <div className="flex flex-wrap gap-2">
              <ReportExcelButton
                model={spec.model}
                id={panelReporte.id}
                action={panelReporte.accion}
                params={panelReporte.params}
              />
              {panelReporte.datos.print != null && (
                <ReportPdfButton print={panelReporte.datos.print} />
              )}
            </div>
          )
        }
        onClose={() => {
          setPanelReporte(null);
        }}
      />
    </>
  );

  return { items, dialogs, ocultas };
}

/**
 * La barra de siempre: los botones uno al lado del otro.
 *
 * Es lo que usa la LISTA, donde la botonera de selección tiene la fila entera
 * para ella y no compite con ningún título. El formulario, en cambio, arma su
 * cabecera con `useActionItems` + `Toolbar` para poder plegar.
 */
export function ActionBar({ spec, ids, scope, size = 'md', blockedReason = null, onDone }: ActionBarProps) {
  const { items, dialogs } = useActionItems({
    spec,
    ids,
    scope,
    blockedReason,
    ...(onDone === undefined ? {} : { onDone }),
  });

  return (
    <>
      {/* `visible` al tope: en la lista no se pliega nada, se dibujan todos. */}
      <Toolbar items={items} visible={items.length} size={size} />
      {dialogs}
    </>
  );
}
